'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useKeenSlider } from 'keen-slider/react';
import { ChevronRight, Home } from 'lucide-react';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useGearUploadStore } from '@/stores/useGearUploadStore';
import { useLabManageStore } from '@/stores/useLabManageStore';
import { useBreadcrumb } from '@/utils/helpers/breadcrumbHelper';
import IconAdaptive from '@/components/IconAdaptive';
import 'keen-slider/keen-slider.min.css';

type ServiceHeaderProps = {
    title: string;
    breadcrumbLeaf?: string;
};

export default function ServiceHeader({ title, breadcrumbLeaf }: ServiceHeaderProps) {
    const { scheme } = useThemeColor();
    const breadcrumb = useBreadcrumb({ leaf: breadcrumbLeaf });
    const [showBreadcrumb, setShowBreadcrumb] = useState(false);
    const editGearId = useGearUploadStore((s) => s.editGearId);
    const editLabId = useLabManageStore((s) => s.editLabId);

    useEffect(() => {
        setShowBreadcrumb(true);
    }, [title]);

    const buildHref = (href?: string) => {
        if (href === '/gears/settings' && editGearId) return `/gears/settings/${editGearId}`;
        if (href === '/labs/settings' && editLabId) return `/labs/settings/${editLabId}`;
        return href;
    };

    // --- Keen Slider: 安定表示用プラグイン（サイズ・フォント読み込み後に update）---
    const stabilizePlugin = (slider: any) => {
        const ro = new ResizeObserver(() => slider.update());
        ro.observe(slider.container);
        slider.on('destroyed', () => ro.disconnect());

        // @ts-ignore
        if (typeof document !== 'undefined' && document.fonts?.ready) {
            // @ts-ignore
            document.fonts.ready.then(() => slider.update());
        }
    };

    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>(
        {
            slides: { perView: 'auto', spacing: 2 },
            mode: 'free',
            renderMode: 'performance',
            rubberband: true,
        },
        [stabilizePlugin],
    );

    useEffect(() => {
        if (!slider) return;
        const id = requestAnimationFrame(() => slider.current?.update());
        return () => cancelAnimationFrame(id);
    }, [slider, showBreadcrumb, breadcrumb.length]);

    return (
        <div className="flex justify-center">
            <div
                className="
          flex flex-col md:flex-row w-[98%]
          px-2 pt-1 md:pt-4 pb-2 md:pb-0
          space-y-1 md:space-y-0 space-x-0 md:space-x-8
          items-start md:items-center
        "
            >
                {/* Title */}
                <div
                    className={`flex font-montserrat font-black ${scheme?.text}
                      text-[20px] md:text-[25px] tracking-widest select-none`}
                >
                    {title
                        .toUpperCase()
                        .split('')
                        .map((char, index) => (
                            <motion.span
                                key={index}
                                initial={{ y: -50, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{
                                    delay: 1.0 + index * 0.1,
                                    type: 'spring',
                                    stiffness: 500,
                                    damping: 20,
                                }}
                            >
                                {char === ' ' ? '\u00A0' : char}
                            </motion.span>
                        ))}
                </div>

                {/* Breadcrumb (共通：スライド) */}
                {showBreadcrumb && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="w-full md:w-auto"
                    >
                        <div
                            ref={sliderRef}
                            className="keen-slider flex items-center font-corp font-medium tracking-widest text-color-base pl-2 pr-2 md:pl-3 md:pr-3"
                        >
                            <div
                                className="keen-slider__slide shrink-0 inline-flex items-center px-1 py-0"
                                style={{ width: 'auto' }}
                            >
                                <Link href="/" className="hover:text-color-service-selected">
                                    <Home size={14} strokeWidth={3} />
                                </Link>
                            </div>
                            {breadcrumb.length > 0 && (
                                <div className="keen-slider__slide shrink-0 mx-1" style={{ width: 'auto' }}>
                                    <IconAdaptive
                                        Icon={ChevronRight}
                                        strokeWidth={2.5}
                                        baseSize={14}
                                        breakpoints={{ md: 16 }}
                                    />
                                </div>
                            )}
                            {breadcrumb.map((item, idx) => {
                                const isLast = idx === breadcrumb.length - 1;
                                const href = buildHref(item.href);

                                return (
                                    <div
                                        key={idx}
                                        className="keen-slider__slide shrink-0 flex items-center gap-1"
                                        style={{ width: 'auto' }}
                                    >
                                        {isLast ? (
                                            <span
                                                className={`font-bold ${scheme?.text} text-sm md:text-[16px] mb-[0.5px] md:mb-[2px]`}
                                                aria-current="page"
                                            >
                                                {item.label}
                                            </span>
                                        ) : (
                                            <>
                                                {href ? (
                                                    <Link
                                                        href={href}
                                                        className="hover:text-color-service-selected text-sm md:text-sm"
                                                        prefetch={false}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                ) : (
                                                    <span className="text-sm md:text-sm">{item.label}</span>
                                                )}
                                                <div className="mx-1">
                                                    <IconAdaptive
                                                        Icon={ChevronRight}
                                                        strokeWidth={2.5}
                                                        baseSize={14}
                                                        breakpoints={{ md: 16 }}
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
